import { Check } from "lucide-react";
import { Link } from "react-router-dom";
import { EXAMS } from "../data/exams";
import { LABS } from "../data/labs";
import type { Lab } from "../data/labTypes";
import { ExamBadge, useDocumentTitle } from "../components/ui";
import { useProgress } from "../lib/store";
import { localizeLab } from "../data/localized";
import { useT, type L } from "../i18n";

export const LAB_TYPE_LABEL: Record<Lab["type"], L> = {
  order: { vi: "Sắp xếp", en: "Ordering" },
  classify: { vi: "Phân loại", en: "Classify" },
  spot: { vi: "Tìm lỗi", en: "Spot the bug" },
  build: { vi: "Tự viết", en: "Build" },
};

export function LabsPage() {
  const t = useT();
  useDocumentTitle(t("Thực hành", "Labs"));
  const p = useProgress();
  const perfect = LABS.filter((l) => p.labs[l.id]?.best === 100).length;

  return (
    <>
      <h1>{t("Lab thực hành", "Hands-on labs")}</h1>
      <p className="muted">
        {t(
          "Học bằng cách làm: viết prompt và kế hoạch eval, thiết kế tool/schema, xử lý tool result, tìm lỗi cấu hình và phân tích rủi ro agent. Chấm ngay trên trình duyệt, không cần API key. Bài viết tự do được chấm bằng heuristic từ khóa, không phải đánh giá ngữ nghĩa; hãy xem nguồn chính thức và tự rà soát trước khi áp dụng.",
          "Learn by doing: write prompts and eval plans, design tools/schemas, handle tool results, find configuration bugs, and assess agent risks. Graded in your browser, no API key needed. Free-text tasks use keyword heuristics, not semantic evaluation; consult the official references and review your work before applying it.",
        )}
      </p>
      <p><span className="chip ok">{t(`${perfect}/${LABS.length} lab đạt 100`, `${perfect}/${LABS.length} labs at 100`)}</span></p>

      {EXAMS.map((e) => {
        const labs = LABS.filter((l) => l.exam === e.id).map((l) => localizeLab(l, p.lang));
        if (!labs.length) return null;
        return (
          <section key={e.id}>
            <div className="stage-head">
              <ExamBadge examId={e.id} />
              <h2 className="mb-0">{e.code} · {e.name.replace("Claude Certified ", "")}</h2>
            </div>
            <div className="grid grid-2">
              {labs.map((l) => {
                const best = p.labs[l.id]?.best;
                return (
                  <Link key={l.id} to={`/labs/${l.id}`} className="card link">
                    <div className="row between">
                      <span className="chip">{t(LAB_TYPE_LABEL[l.type].vi, LAB_TYPE_LABEL[l.type].en)}</span>
                      {best !== undefined ? (
                        <span className={`chip ${best === 100 ? "ok" : "warn"}`}>{best === 100 ? <><Check className="ico" size={14} aria-hidden="true" /> 100</> : `${best}/100`}</span>
                      ) : (
                        <span className="small muted">{l.minutes} {t("phút", "min")}</span>
                      )}
                    </div>
                    <h3 className="mt">{l.title}</h3>
                    <p className="small muted mb-0">{l.skill} — {l.intro.length > 110 ? `${l.intro.slice(0, 110)}…` : l.intro}</p>
                  </Link>
                );
              })}
            </div>
          </section>
        );
      })}
    </>
  );
}
